import React from 'react';
import { useMutation } from 'react-apollo';
import pluralize from 'pluralize';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Theme,
} from '@material-ui/core';
import { useModal } from 'src/app-builder';

import { useNotification } from 'src/hooks';
import { SubmitButton } from 'src/components';
import { DOCUMENTS_DELETE_MUTATION } from 'src/graphql';
import { DIALOGS } from './constants';
import { isFunction, t } from 'src/utils';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: theme.spacing(60),
  },
}));

interface DocumentDeleteDialogProps {}

export const DocumentDeleteDialog: React.FC<DocumentDeleteDialogProps> = () => {
  const classes = useStyles();

  const notification = useNotification();

  const { open, args, closeModal } = useModal(DIALOGS.DOCUMENT_DELETE_DIALOG);

  const documentsCount = Array.isArray(args?.id) ? args?.id?.length : 1;
  const formattedDocuments = pluralize('Documents', documentsCount);

  const [deleteDocument, { loading }] = useMutation(DOCUMENTS_DELETE_MUTATION, {
    onError: () =>
      notification.error(
        t('document_delete_error', {
          formattedDocuments,
        }),
      ),
  });

  const onDelete = React.useCallback(async () => {
    if (Array.isArray(args?.id)) {
      await Promise.all(
        args?.id.slice(1).map((id: string) =>
          deleteDocument({
            variables: { filter: { id }, force: true },
          }),
        ),
      );

      await deleteDocument({
        variables: { filter: { id: args?.id[0] }, force: true },
        refetchQueries: ['DocumentsTableContent'],
        awaitRefetchQueries: true,
      });
    } else {
      await deleteDocument({
        variables: { filter: { id: args?.id }, force: true },
        refetchQueries: ['DocumentsTableContent'],
        awaitRefetchQueries: true,
      });
    }

    notification.success(
      t('document_delete_success', {
        formattedDocuments,
      }),
    );

    if (isFunction(args?.onSuccess)) {
      args.onSuccess();
    }

    closeModal();
  }, [closeModal, args, deleteDocument, notification, formattedDocuments]);

  const onClose = React.useCallback(() => {
    closeModal();
  }, [closeModal]);

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" PaperProps={{ className: classes.root }}>
      <DialogTitle>Delete {formattedDocuments}</DialogTitle>

      <DialogContent>
        <DialogContentText>
          Are you sure want to delete this {formattedDocuments.toLowerCase()}?
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <SubmitButton
          color="secondary"
          variant="contained"
          type="submit"
          onClick={onDelete}
          loading={loading}
        >
          Delete {formattedDocuments}
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
};
